/**
 * AppointmentFilterBar — the filter header + side drawer + active-filter
 * chips for the patient appointments tab.
 *
 * Flutter ref: `patient_appointments_tab.dart`
 *   - `calendarOptionsHeader()` (:455): Statuses popover (left) + location
 *     icon row (right) + "Filters" link that opens the side drawer.
 *   - `statusesPopup()` (:709): 270-wide popover, Select All / Reset + a
 *     checkbox per status (colored IndicatorTag).
 *   - `appointmentLocationFilterWidget()` (:504): one toggle icon per org
 *     location.
 *   - `showFilteredList()` (:617): removable chips for the active date-range
 *     + each selected status (NO chips for practice/doctor/location — Flutter
 *     parity).
 *   - `patient_appointments_filters.dart`: the drawer body — Date range /
 *     Practice / Doctors / Status / Location sections, each with its own
 *     Reset, plus a global Reset All.
 *
 * React idiom (CLAUDE.md Flutter-workaround simplification): canvas popover →
 * shadcn Popover; side drawer → Signal DS `Drawer`; date range → shadcn
 * Calendar (range) in a Popover; checkboxes → Signal DS `Checkbox`.
 */

import { useState } from 'react';
import { Filter } from 'lucide-react';
import { locationGlyph } from '@/scheduling/lib/appointment-glyphs';
import {
  Tag,
  DateRangePicker,
  type DateRange,
  Drawer,
  Checkbox,
} from '@scripsteam/scrips-signal-ds';
import { cn } from '@/lib/utils';
import { AnchoredPopover, AnchoredPopoverContent, AnchoredPopoverTrigger, Button } from '@scripsteam/scrips-signal-ds';
import { AppointmentStatusBadge } from './appointment-status-badge';
import type { OrgLocation } from '@/auth/auth-api';
import type {
  AppointmentFacetPractice,
  AppointmentFacetProvider,
  AppointmentStatus,
} from '@/types/scheduling-api';

export interface AppointmentFilterState {
  status: AppointmentStatus[];
  location: string[];
  practices: string[];
  practitioners: string[];
  /** Local ISO (no Z), or null when unset. */
  startDate: string | null;
  endDate: string | null;
}

interface AppointmentFilterBarProps {
  filters: AppointmentFilterState;
  statusList: AppointmentStatus[];
  locations: OrgLocation[];
  practices: AppointmentFacetPractice[];
  providers: AppointmentFacetProvider[];
  onChange: (next: AppointmentFilterState) => void;
}

const fmtDate = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString(undefined, {
        month: 'short',
        day: '2-digit',
        year: 'numeric',
      });
};

/** Location name → icon (Flutter IconHelper.getLocationIcon, matches calendar). */
/** The location's glyph: the appointment glyph map's one set (Plus / Home / Video / Ban; Ban for an unknown name). */
const iconForLocation = (name: string) => locationGlyph(name, 'block');

/** Local-midnight ISO without the `Z` suffix (.NET binder, CLAUDE.md). */
const toLocalIso = (d: Date): string => {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T00:00:00`;
};

export function AppointmentFilterBar({
  filters,
  statusList,
  locations,
  practices,
  providers,
  onChange,
}: AppointmentFilterBarProps) {
  // PROD-2743 · switch ON: the location toggles draw the DS appointment glyphs. OFF = today.
  const [statusOpen, setStatusOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);

  const set = (patch: Partial<AppointmentFilterState>) =>
    onChange({ ...filters, ...patch });

  const toggle = <T,>(list: T[], item: T, eq: (a: T, b: T) => boolean): T[] =>
    list.some((x) => eq(x, item))
      ? list.filter((x) => !eq(x, item))
      : [...list, item];

  const toggleStatus = (s: AppointmentStatus) =>
    set({ status: toggle(filters.status, s, (a, b) => a.code === b.code) });
  const toggleId =
    (key: 'location' | 'practices' | 'practitioners') => (id: string) =>
      set({ [key]: toggle(filters[key], id, (a, b) => a === b) });

  // Practice facet ships duplicates — dedupe by id (verified 2026-06-02).
  const uniquePractices = dedupeById(practices);

  const anyActive =
    filters.status.length > 0 ||
    filters.location.length > 0 ||
    filters.practices.length > 0 ||
    filters.practitioners.length > 0 ||
    filters.startDate != null ||
    filters.endDate != null;

  // Always a defined DateRange (never `undefined`) so DS `DateRangePicker`
  // stays fully controlled — passing `undefined` makes it fall back to its
  // own uncontrolled `internal` state, which does not clear on Reset / Reset
  // All (the old fully-controlled `Calendar` cleared correctly on both).
  const dateRange: DateRange = filters.startDate
    ? {
        from: new Date(filters.startDate),
        to: filters.endDate ? new Date(filters.endDate) : undefined,
      }
    : { from: undefined };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        {/* Statuses popover */}
        <AnchoredPopover open={statusOpen} onOpenChange={setStatusOpen}>
          <AnchoredPopoverTrigger
            render={
              <button
                type="button"
                className={cn(
                  'flex items-center gap-2 text-sm',
                  filters.status.length > 0
                    ? 'text-brand-primary'
                    : 'text-text-primary',
                )}
              />
            }
          >
            <Filter className="size-4" aria-hidden />
            <span>Statuses</span>
          </AnchoredPopoverTrigger>
          <AnchoredPopoverContent collisionAvoidance={{ fallbackAxisSide: 'end' }} align="start" className="w-67.5 p-4 gap-3">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-sm font-bold uppercase text-text-primary">
                Appointment Status
              </span>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  className="text-sm font-bold text-brand-primary"
                  onClick={() => set({ status: [...statusList] })}
                >
                  Select All
                </button>
                <button
                  type="button"
                  disabled={filters.status.length === 0}
                  className="text-sm font-bold text-brand-primary disabled:text-text-hint"
                  onClick={() => set({ status: [] })}
                >
                  Reset
                </button>
              </div>
            </div>
            <div className="space-y-3">
              {statusList.map((s) => (
                <Checkbox
                  key={s.code}
                  checked={filters.status.some((x) => x.code === s.code)}
                  onCheckedChange={() => toggleStatus(s)}
                  label={<AppointmentStatusBadge status={s.display ?? s.code} />}
                  className="gap-3"
                />
              ))}
            </div>
          </AnchoredPopoverContent>
        </AnchoredPopover>

        <div className="flex-1" />

        {/* Location toggle icons — per-type icon by name (Flutter
            IconHelper.getLocationIcon: Office/Home/Virtual). */}
        <div className="flex items-center gap-1">
          {locations.map((loc) => {
            const active = filters.location.includes(loc.id);
            const Glyph = iconForLocation(loc.name);
            return (
              <button
                key={loc.id}
                type="button"
                aria-label={loc.name}
                aria-pressed={active}
                onClick={() => toggleId('location')(loc.id)}
                // The app icon button; on = the selected blue (`aria-pressed`), off = secondary ink (was hint ink).
                className="icon-action"
              >
                <Glyph className="size-4" aria-hidden />
              </button>
            );
          })}
        </div>

        {/* Filters drawer trigger */}
        <button
          type="button"
          className="text-sm font-semibold text-brand-primary"
          onClick={() => setSheetOpen(true)}
        >
          Filters
        </button>

        <Drawer
          open={sheetOpen}
          onClose={() => setSheetOpen(false)}
          side="right"
          width={420}
          title="Filters"
          className="overflow-y-auto scrollbar-hide"
          // The design's drawer (appointments-filters.html): 420 wide, Reset all (secondary) and Apply filters (primary)
          // in the footer. Filters apply as they change (unchanged); Apply filters closes the drawer.
          footer={
            <div data-slot="filter-drawer-footer">
              <Button
                type="button"
                variant="secondary"
                disabled={!anyActive}
                onClick={() =>
                set({
                  status: [],
                  location: [],
                  practices: [],
                  practitioners: [],
                  startDate: null,
                  endDate: null,
                })
              }
              >
                Reset all
              </Button>
              <Button type="button" onClick={() => setSheetOpen(false)}>
                Apply filters
              </Button>
            </div>
          }
        >
            <div className="space-y-6 px-4 pb-8">
              {/* Date range */}
              <Section
                title="Appointments Date Range"
                onReset={() => set({ startDate: null, endDate: null })}
                resetEnabled={filters.startDate != null || filters.endDate != null}
              >
                <DateRangePicker
                  size="compact"
                  placeholder="Select date range"
                  value={dateRange}
                  onChange={(r) =>
                    set({
                      startDate: r?.from ? toLocalIso(r.from) : null,
                      endDate: r?.to ? toLocalIso(r.to) : null,
                    })
                  }
                />
              </Section>

              {/* Practice */}
              <Section
                title="Practice"
                onReset={() => set({ practices: [] })}
                resetEnabled={filters.practices.length > 0}
              >
                {uniquePractices.map((p) => (
                  <CheckRow
                    key={p.id}
                    label={p.name ?? '—'}
                    checked={filters.practices.includes(p.id)}
                    onToggle={() => toggleId('practices')(p.id)}
                  />
                ))}
              </Section>

              {/* Doctors */}
              <Section
                title="Doctors"
                onReset={() => set({ practitioners: [] })}
                resetEnabled={filters.practitioners.length > 0}
              >
                {providers.map((p) => (
                  <CheckRow
                    key={p.id}
                    label={p.name ?? '—'}
                    checked={filters.practitioners.includes(p.id)}
                    onToggle={() => toggleId('practitioners')(p.id)}
                  />
                ))}
              </Section>

              {/* Status */}
              <Section
                title="Status"
                onReset={() => set({ status: [] })}
                resetEnabled={filters.status.length > 0}
              >
                {statusList.map((s) => (
                  <CheckRow
                    key={s.code}
                    label={s.code}
                    checked={filters.status.some((x) => x.code === s.code)}
                    onToggle={() => toggleStatus(s)}
                  />
                ))}
              </Section>

              {/* Location */}
              <Section
                title="Location"
                onReset={() => set({ location: [] })}
                resetEnabled={filters.location.length > 0}
              >
                {locations.map((loc) => (
                  <CheckRow
                    key={loc.id}
                    label={loc.name}
                    checked={filters.location.includes(loc.id)}
                    onToggle={() => toggleId('location')(loc.id)}
                  />
                ))}
              </Section>
            </div>
        </Drawer>
      </div>

      {/* Active-filter chips (date + status, Flutter parity) */}
      {(filters.startDate || filters.status.length > 0) && (
        <div className="flex flex-wrap gap-2">
          {filters.startDate ? (
            <Tag
              removeLabel={`Remove ${fmtDate(filters.startDate)}${
                filters.endDate ? ` - ${fmtDate(filters.endDate)}` : ''
              }`}
              onRemove={() => set({ startDate: null, endDate: null })}
            >
              {`${fmtDate(filters.startDate)}${
                filters.endDate ? ` - ${fmtDate(filters.endDate)}` : ''
              }`}
            </Tag>
          ) : null}
          {filters.status.map((s) => (
            <Tag
              key={s.code}
              removeLabel={`Remove ${s.code}`}
              onRemove={() => toggleStatus(s)}
            >
              {s.code}
            </Tag>
          ))}
        </div>
      )}
    </div>
  );
}

function Section({
  title,
  onReset,
  resetEnabled,
  children,
}: {
  title: string;
  onReset: () => void;
  resetEnabled: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-bold uppercase tracking-wide text-text-muted">
          {title}
        </span>
        <button
          type="button"
          disabled={!resetEnabled}
          onClick={onReset}
          className="text-sm font-semibold text-brand-primary disabled:text-text-hint"
        >
          Reset
        </button>
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function CheckRow({
  label,
  checked,
  onToggle,
}: {
  label: string;
  checked: boolean;
  onToggle: () => void;
}) {
  return (
    <Checkbox
      checked={checked}
      onCheckedChange={onToggle}
      label={<span className="text-sm text-text-primary">{label}</span>}
      className="gap-2"
    />
  );
}

function dedupeById<T extends { id: string }>(items: T[]): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const it of items) {
    if (!seen.has(it.id)) {
      seen.add(it.id);
      out.push(it);
    }
  }
  return out;
}
